/**
 * The studios page (container, D25's admin screen): the list and the create
 * dialog. "From a template" builds the studio (devices and mappings, D32)
 * from the one template source (`@acp/engine/templates.js`, what `acp
 * studio-template [--kind vmix]` prints): the simulators on the host and port
 * block the page's `?simHost=&simBasePort=` name (`pnpm dev:studio` prints
 * that link) or the primary checkout's, or one vMix on its host and TCP port.
 * Its default show style is an existing one, none, or a new one made from the
 * template kind's show style first (`acp show-style-template [--kind vmix]`).
 */
import { showStyleTemplateOf, studioTemplateOf } from '@acp/engine/templates.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateShowStyle, useCreateStudio, useGrants, useShowStyles, useStudios } from '../app/hooks.js';
import { CreateStudioDialog, type StudioRequest } from '../components/studios/CreateStudioDialog.js';
import { StudioList, studioHref } from '../components/studios/StudioList.js';
import { allowsSomewhere } from '../lib/permissions.js';
import { simDefaultsFrom } from '../lib/studio/simDefaults.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function StudiosView() {
  const studios = useStudios();
  const showStyles = useShowStyles();
  const create = useCreateStudio();
  const createShowStyle = useCreateShowStyle();
  const grants = useGrants();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sim] = useState(() => simDefaultsFrom(typeof window === 'undefined' ? '' : window.location.search));
  // The template show style's id when the studio has none of its own: stable across a failed attempt's retries, fresh after a success.
  const [templateShowStyleId, setTemplateShowStyleId] = useState(() => `show-style-${crypto.randomUUID()}`);

  const submit = async (request: StudioRequest): Promise<void> => {
    const config = request.template === null ? {} : studioTemplateOf(request.template);
    let defaultShowStyleId: string | null = null;
    if (request.defaultShowStyle?.kind === 'existing') defaultShowStyleId = request.defaultShowStyle.showStyleId;
    if (request.defaultShowStyle?.kind === 'template') {
      // A client-chosen id (the studio's, when it has one): a retry after a failed studio create finds the same show style
      // (a create of an existing id answers it as it is) instead of making another copy (review finding).
      const made = await createShowStyle.mutateAsync({ showStyleId: request.studioId ?? templateShowStyleId, name: `${request.name} show style`, config: showStyleTemplateOf(request.template?.kind ?? 'sim') });
      defaultShowStyleId = made.showStyleId;
    }
    const studio = await create.mutateAsync({ studioId: request.studioId, name: request.name, siteId: request.siteId, defaultShowStyleId, config });
    setTemplateShowStyleId(`show-style-${crypto.randomUUID()}`);
    setOpen(false);
    toast.success(`Created “${studio.name}”`);
    void navigate({ to: studioHref(studio.studioId) });
  };

  const failure = create.error ?? createShowStyle.error;
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <StudioList
        studios={studios.data ?? []}
        loading={studios.isFetching}
        error={studios.error === null ? null : messageOf(studios.error)}
        canCreate={allowsSomewhere(grants, 'device.configure')}
        onCreate={() => {
          create.reset();
          createShowStyle.reset();
          setOpen(true);
        }}
        onRetry={() => {
          void studios.refetch();
        }}
        onOpen={(studioId) => {
          void navigate({ to: studioHref(studioId) });
        }}
      />
      <CreateStudioDialog
        open={open}
        onOpenChange={setOpen}
        defaultHost={sim.host}
        defaultBasePort={sim.basePort}
        showStyles={(showStyles.data ?? []).map((s) => ({ id: s.showStyleId, name: s.name }))}
        pending={create.isPending || createShowStyle.isPending}
        error={failure === null ? null : messageOf(failure)}
        onSubmit={(request) => {
          // A failure is shown in the dialog (the mutations' errors); nothing else to do here.
          submit(request).catch(() => undefined);
        }}
      />
    </div>
  );
}
